
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import { getNestedValue } from '../utils/dataBinding';

interface ImageComponentProps {
    component: ComponentNode;
    data?: any;
    context?: {
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
    };
}

const ImageComponent: React.FC<ImageComponentProps> = ({ component, data, context }) => {
    const { data: componentData, placeholder, styles, href, alt, customClassName } = component;
    const inlineStyles = convertStylesToInline(styles);

    const resolvedSrc = (() => {
        if (placeholder && data) {
            const dynamicValue = getNestedValue(data, placeholder);
            const placeholderValue =
                typeof dynamicValue === 'string' ? dynamicValue.trim() : dynamicValue;

            return (
                placeholderValue || (
                    data.image ??
                    data.icon ??
                    data.category_image ??
                    data.category_icon ??
                    data.featured_image ??
                    data.thumbnail ??
                    data.banner_image ??
                    data.merchant_logo ??
                    componentData
                )
            );
        }

        return (
            componentData ||
            data?.image ||
            data?.icon ||
            data?.category_image ||
            data?.category_icon ||
            data?.featured_image ||
            data?.thumbnail ||
            data?.banner_image ||
            data?.merchant_logo ||
            ''
        );
    })();

    const normalizeImageSrc = (src: string) => {
        const trimmed = src.trim();
        if (!trimmed) return '';

        if (/^(https?:\/\/|\/\/|data:|blob:)/i.test(trimmed)) {
            return trimmed;
        }

        return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    };

    const imageSrc = typeof resolvedSrc === 'string' ? normalizeImageSrc(resolvedSrc) : '';
    const resolvedHref = resolveCustomThemeHref(href, data, context);

    if (!imageSrc) {
        return null;
    }

    // `fill` mode stretches to 100% of its wrapper in both dimensions, so the
    // wrapper needs a real (non-"auto") size on both axes or it collapses to
    // 0 — confirmed live on merchant/offer card logos that only set a
    // max-width/max-height cap with no base size. A node asking for
    // `width: "auto"` (every header/footer brand logo: fixed height, natural
    // aspect ratio) can't be expressed as `fill` at all; it needs real
    // intrinsic sizing instead, so it renders through next/image's normal
    // width+height path rather than fill.
    const wantsAutoWidth = styles?.width === 'auto' || (!styles?.width && styles?.height);
    const wantsNaturalHeight = styles?.height === 'auto';
    const isOfferBanner = placeholder === 'offer.banner_image';
    const isHeroBannerImage = placeholder === 'slider_image';
    const objectFit = (styles?.objectFit as React.CSSProperties['objectFit']) || 'contain';
    const imageClassName = [customClassName, isHeroBannerImage ? 'cb-hero-banner-image' : undefined]
        .filter(Boolean)
        .join(' ');

    const imageElement = wantsNaturalHeight ? (
        // Natural-size images don't have intrinsic dimensions in the template
        // JSON, so next/image cannot render this branch during SSR.
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={imageSrc}
            alt={alt || 'Dynamic content'}
            style={{
                ...inlineStyles,
                ...(isOfferBanner ? { maxWidth: styles?.maxWidth || '300px', margin: styles?.margin || '0 auto' } : {}),
                objectFit,
            }}
            className={imageClassName}
        />
    ) : wantsAutoWidth ? (
        <span style={{ display: 'inline-block', lineHeight: 0, ...inlineStyles, width: 'auto' }} className={imageClassName}>
            <Image
                src={imageSrc}
                alt={alt || 'Dynamic content'}
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: 'auto', height: '100%', objectFit }}
                unoptimized
            />
        </span>
    ) : (
        <div style={{ position: 'relative', ...inlineStyles }} className={imageClassName}>
            <Image
                src={imageSrc}
                alt={alt || 'Dynamic content'}
                fill
                style={{ objectFit }}
                unoptimized
            />
        </div>
    );

    if (resolvedHref) {
        return <Link href={resolvedHref}>{imageElement}</Link>;
    }

    return imageElement;
};

export default ImageComponent;